import type { SaleCrmStageCount, SaleListRow } from 'services/types/sale';

import baseApi from './baseApi';

/** Filters shared by the list and the tab counts, so both always describe the same row set. */
export type SalesFilterParams = {
  businessUnitId: number;
  searchTerm?: string;
  /** Comma-separated executive ids — the sale's executive is the one assigned to its client. */
  userIds?: string;
  uploadedFrom?: string;
  uploadedTo?: string;
};

export type GetSalesParams = SalesFilterParams & {
  paginationModel: { page: number; pageSize: number };
  stage?: string;
  subStage?: string;
};

const toQueryString = (params: Record<string, string | undefined>): string =>
  new URLSearchParams(Object.entries(params).filter((entry): entry is [string, string] => !!entry[1])).toString();

/** Built once so the list and the counts can never be sent a different filter set. */
const filterParams = (params: SalesFilterParams): Record<string, string | undefined> => ({
  businessUnitId: String(params.businessUnitId),
  searchTerm: params.searchTerm,
  userIds: params.userIds,
  uploadedFrom: params.uploadedFrom,
  uploadedTo: params.uploadedTo,
});

const salesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getSales: build.query<{ sales: SaleListRow[]; total: number }, GetSalesParams>({
      query: (params) => ({
        url: `/sales?${toQueryString({
          ...filterParams(params),
          paginationModel: JSON.stringify(params.paginationModel),
          stage: params.stage,
          subStage: params.subStage,
        })}`,
        method: 'GET',
      }),
      providesTags: [{ type: 'Sales' as const, id: 'LIST' }],
    }),
    getSalesCrmStages: build.query<{ stages: SaleCrmStageCount[] }, SalesFilterParams>({
      query: (params) => ({
        url: `/sales/crm-stages?${toQueryString(filterParams(params))}`,
        method: 'GET',
      }),
      providesTags: [{ type: 'Sales' as const, id: 'LIST' }],
    }),
  }),
});

export const { useGetSalesQuery, useGetSalesCrmStagesQuery } = salesApi;
